import { ArrowRightOutlined, CalendarOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Col,
  Image,
  List,
  Pagination,
  Row,
  Segmented,
  Tooltip,
  Typography,
  message,
  Space,
  Select,
  Spin,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPaginationProps } from "../../utils/tablePagination";
import bedImg from "../../assets/images/patientIcons/bed.svg";
import docIcon from "../../assets/doctorIcons/consultant.svg";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { getPatientListApi } from "../../services/patient.services";
import { floorListApi } from "../../services/nurse.services";
import { getCookie } from "../../helpers/localStorage";
import { FacilityID } from "../../constants/defaultKeys";
import "./PatientList.css";
import NoDataPage from "../NoDataPages/NoDataPage";
import PatientDataIcon from "../../assets/images/nurseIcons/patientdata.svg";
import useQuery from "../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
  parseFloorParam,
} from "../../helpers/urlFilters";
import NutritionistPatientTable from "./NutritionistPatientTable";
import { UiButton, UiTag, UiDataTable } from "../ui";
import { formatPatientAge } from "../../helpers/formatAge";
import usePageFillTableScroll from "../../hooks/usePageFillTableScroll";
import useIsMobile from "../../hooks/useIsMobile";

dayjs.extend(relativeTime);

const InPatientsCard = ({ userDetails, role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQuery();
  const isMobile = useIsMobile();
  const listWrapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [activeInSegment, setActiveInSegment] = useState(
    () => params?.status || "current"
  );
  const [patientList, setPatientList] = useState([]);
  const [pageNo, setPageNo] = useState(parsePageParam(params));
  const [searchQuery, setSearchQuery] = useState(
    () => parseSearchParam(params) ?? ""
  );
  const [floor, setFloor] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({
    current: null,
    older: null,
  });
  usePageFillTableScroll(
    listWrapRef,
    isMobile,
    "default",
    patientList?.data?.length ?? 0
  );
  const [selectedFloor, setSelectedFloor] = useState(parseFloorParam(params));
  const inPatientLabels = [
    { label: "Active", value: "current" },
    { label: "Discharged", value: "older" },
  ];
  const [assessmentDate, setAssessmentDate] = useState(() =>
    params?.assessment_date ? dayjs(params.assessment_date) : null
  );

  const facilityId = Number(getCookie(FacilityID)) || userDetails?.currFacility;

  useEffect(() => {
    floorListApi(`?facility=${facilityId}`)
      .then((res) => {
        setFloor(res?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching floors:", err);
      });
  }, [facilityId]);

  useEffect(() => {
    setSegmentCounts({ current: null, older: null });
  }, [facilityId, selectedFloor, searchQuery, assessmentDate]);

  const fetchPatientList = () => {
    setLoading(true);
    const floorParam = selectedFloor ? `&floor=${selectedFloor}` : "";
    const searchParam = searchQuery ? `&search=${searchQuery}` : "";
    const assessmentDateParam = assessmentDate
      ? `&assessment_date=${dayjs(assessmentDate).format("YYYY-MM-DD")}`
      : "";

    getPatientListApi(
      `facility=${facilityId}${floorParam}${searchParam}${assessmentDateParam}`,
      activeInSegment,
      pageNo,
      searchQuery
    )
      .then((res) => {
        setPatientList(res?.data);
        setSegmentCounts((prev) => ({
          ...prev,
          [activeInSegment]: res?.data?.total_count ?? 0,
        }));
      })
      .catch((err) => {
        message.error("Failed to fetch patient list.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPatientList();
  }, [
    activeInSegment,
    pageNo,
    facilityId,
    searchQuery,
    selectedFloor,
    assessmentDate,
  ]);

  // Keep filters aligned when URL changes (e.g. In/Out patient switch clears search)
  useEffect(() => {
    const nextSegment = params?.status || "current";
    const nextSearch = parseSearchParam(params) ?? "";
    const nextPage = parsePageParam(params);
    setActiveInSegment((prev) => (prev === nextSegment ? prev : nextSegment));
    setSearchQuery((prev) => (prev === nextSearch ? prev : nextSearch));
    setPageNo((prev) => (prev === nextPage ? prev : nextPage));
  }, [location.search, params?.status, params?.search, params?.page]);

  const onInpatientSegmentChange = (value) => {
    if (value === activeInSegment) return;
    updateUrlSearchParams(navigate, {
      status: value,
      search: null,
      page: null,
      assessment_date: null,
    });
    setLoading(true);
    setPatientList({ data: [], total_count: 0, total_pages: 0 });
    setActiveInSegment(value);
    setSearchQuery("");
    setAssessmentDate(null);
    setPageNo(1);
  };

  const onFloorChange = (value) => {
    setSelectedFloor(value);
    setPageNo(1);
    updateUrlSearchParams(navigate, { floor: value || null, page: null });
  };

  const truncateName = (name, role) => {
    if (role === "patient") {
      return name && name.length > 28 ? `${name.slice(0, 28)}...` : name;
    }
    if (role === "consultant") {
      return name && name.length > 18 ? `${name.slice(0, 18)}...` : name;
    }
    return name;
  };

  const formatPatientDates = (item) => {
    const admission = item?.date_of_admission || item?.admission_date;
    if (item?.discharge_date) {
      return `${dayjs(admission).format("DD MMM, YYYY")} - ${dayjs(
        item?.discharge_date
      ).format("DD MMM, YYYY")}`;
    }
    return dayjs(admission).format("DD MMM, YYYY");
  };

  const handlePatientViewClick = (item) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("patient", item?.id);
    newUrl.searchParams.set(
      "type",
      [
        "physiotherapist",
        "speech-swallow-therapist",
        "respiratory-therapist",
        "nutritionist",
        "psychologist",
        "clinical-practitioner",
      ].includes(role)
        ? "ia"
        : "landing"
    );
    navigate(`${newUrl.pathname}${newUrl.search}`, { replace: true });
  };

  const onContactSearch = (value) => {
    const query = value?.target?.value ?? "";
    setSearchQuery(query);
    setPageNo(1);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPageNo(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };

  const inPatientLabelsWithCount = inPatientLabels.map((option) => {
    const count = segmentCounts[option.value];
    const isActiveOption = option.value === activeInSegment;
    const showLoading = isActiveOption && count == null;

    return {
      ...option,
      label: showLoading ? (
        <span className="patient-list-segment-label">
          {option.label}{" "}
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />}
            size="small"
          />
        </span>
      ) : count == null ? (
        option.label
      ) : (
        `${option.label} (${count})`
      ),
    };
  });

  // Called from NutritionistPatientTable column filter → triggers API via assessmentDate state
  const onAssessmentDateChange = (date) => {
    setAssessmentDate(date);
    setPageNo(1);
    updateUrlSearchParams(navigate, {
      assessment_date: date ? dayjs(date).format("YYYY-MM-DD") : null,
      page: null,
    });
  };

  // ── Shared floor + segmented controls ─────────────────────────────────────
  const floorAndSegment = (
    <Space size={8} className="patient-list-toolbar-filters" align="center">
      <Select
        className="patient-floor-select"
        style={{ width: 200, textAlign: "left" }}
        placeholder="Select Floor"
        value={selectedFloor}
        onChange={onFloorChange}
        optionLabelProp="label"
        popupClassName="patient-floor-select-dropdown"
        popupMatchSelectWidth={false}
      >
        <Select.Option key="all" value={0} label="All Floors">
          All Floors
        </Select.Option>
        {floor.map((item) => (
          <Select.Option
            key={item.id}
            value={item.id}
            label={`${item.floor_no} - ${item.building_name}`}
            title={`${item.floor_no} - ${item.building_name}`}
          >
            {item.floor_no} - {item.building_name}
          </Select.Option>
        ))}
      </Select>
      <Segmented
        key={`ip-segment-${activeInSegment}-${segmentCounts[activeInSegment] ?? "loading"}`}
        className="patient-list-segment patient-list-segment--counts"
        options={inPatientLabelsWithCount}
        value={activeInSegment}
        onChange={onInpatientSegmentChange}
      />
    </Space>
  );

  return (
    <>
      <Row>
        <Col xs={24}>
          {role === "nutritionist" ? (
            <UiDataTable
              showHeader={false}
              shellClassName="ui-table-fill-shell"
              onSearch={(e) => onContactSearch(e)}
              searchValue={searchQuery || ""}
              searchPlaceholder="Search for patient"
              toolbarTrailing={floorAndSegment}
            >
              <NutritionistPatientTable
                patientList={patientList}
                loading={loading}
                onPatientClick={handlePatientViewClick}
                assessmentDate={assessmentDate}
                onAssessmentDateChange={onAssessmentDateChange}
                activeInSegment={activeInSegment}
              />
            </UiDataTable>
          ) : (
            <UiDataTable
              showHeader={false}
              shellClassName="ui-table-fill-shell"
              onSearch={(e) => onContactSearch(e)}
              searchValue={searchQuery || ""}
              searchPlaceholder="Search for patient"
              toolbarTrailing={floorAndSegment}
            >
            <div ref={listWrapRef} className="ui-table-fill-body">
            <div className="patient-list-scroll">
            <List
              className={[
                "patient-list",
                !(patientList?.data?.length > 0) ? "patient-list--empty" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              bordered
              header={
                patientList?.data?.length > 0 ? (
                  <div className="patient-list-header ip-patient-row ip-patient-row--header">
                    <div className="ip-patient-row__identity">Name</div>
                    <div className="ip-patient-row__ipid">IP ID</div>
                    <div className="ip-patient-row__date">
                      {activeInSegment === "older"
                        ? "Admission - Discharge"
                        : "Admission Date"}
                    </div>
                    <div className="ip-patient-row__doctor">Doctor</div>
                    <div className="ip-patient-row__action">Action</div>
                  </div>
                ) : null
              }
              pagination={false}
              loading={loading}
              itemLayout="horizontal"
              size="small"
              dataSource={patientList?.data || []}
              rowKey="id"
              style={{ background: "#fff" }}
              locale={{
                emptyText: (
                  <NoDataPage
                    title="No In Patients Available"
                    subtitle="Please check back later or adjust your filters."
                    image={PatientDataIcon}
                  />
                ),
              }}
              renderItem={(item) => (
                <List.Item
                  className="patient-list-row-clickable"
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePatientViewClick(item)}
                >
                  <div className="ip-patient-row">
                    <div className="ip-patient-row__identity">
                      <Typography.Text className="ip-patient-row__name" strong>
                        {truncateName(item?.patient_name, "patient")}
                      </Typography.Text>
                      <Space size={[6, 6]} wrap className="ip-patient-row__meta">
                        <UiTag tone="neutral" size="sm">
                          {formatPatientAge(
                            item?.patient_age_by_dob,
                            item?.patient_gender
                          )}
                        </UiTag>
                        {item?.bed_name && (
                          <UiTag tone="warning" size="sm">
                            <Image src={bedImg} preview={false} />{" "}
                            {item?.bed_name}
                          </UiTag>
                        )}
                      </Space>
                    </div>

                    <div className="ip-patient-row__ipid">
                      {item?.ip_id && (
                        <UiTag
                          tone="neutral"
                          size="sm"
                          className="ip-patient-row__ipid-tag"
                        >
                          {item?.ip_id}
                        </UiTag>
                      )}
                    </div>

                    <div className="ip-patient-row__date">
                      <UiTag tone="info" size="sm">
                        <CalendarOutlined style={{ marginRight: "4px" }} />
                        {formatPatientDates(item)}
                      </UiTag>
                    </div>

                    <div className="ip-patient-row__doctor">
                      {item?.doctor_name && (
                        <Tooltip
                          placement="top"
                          title={item?.doctor_name}
                          color="#E6F4FF"
                          overlayInnerStyle={{ color: "#0d439e" }}
                        >
                          <UiTag
                            tone="info"
                            size="sm"
                            className="ip-patient-row__doctor-tag"
                          >
                            <Image
                              src={docIcon}
                              style={{ width: "12px", marginRight: "4px" }}
                              preview={false}
                            />
                            <span className="ip-patient-row__doctor-name">
                              {truncateName(item?.doctor_name, "consultant")}
                            </span>
                          </UiTag>
                        </Tooltip>
                      )}
                    </div>

                    <div className="ip-patient-row__action">
                      <UiButton
                        variant="text"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePatientViewClick(item);
                        }}
                      >
                        View EMR <ArrowRightOutlined />
                      </UiButton>
                    </div>
                  </div>
                </List.Item>
              )}
            />
            </div>
            {patientList?.total_pages > 1 ? (
              <div className="ui-table-shell-pagination">
                <Pagination
                  {...getPaginationProps({
                    current: pageNo,
                    pageSize: 20,
                    total: patientList?.total_count,
                    onChange: handlePageChange,
                  })}
                />
              </div>
            ) : null}
            </div>
            </UiDataTable>
          )}
        </Col>
      </Row>
    </>
  );
};

export default InPatientsCard;
